/* eslint-disable no-console */
import { ApolloClient, ApolloLink } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { HttpLink } from "@apollo/client/link/http";
import { ApolloProvider } from "@apollo/client/react/context";
import {
  useFragment,
  useMutation,
  useApolloClient,
  useQuery,
  useLazyQuery,
  useSuspenseQuery,
} from "@apollo/client/react/hooks";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getSession } from "store/currentUser/currentUserSelector";
import { currentUserActions } from "store/currentUser/currentUserSlice";

import cache from "./cache";

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  console.log(
    `[GraphQL error]: Operation: ${
      operation.operationName
    }, variables: ${JSON.stringify(operation.variables)}`,
  );
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  if (networkError) console.error(`[Network error]: ${networkError}`);
});

// Handles the Apollo client, as well as refretching access tokens to use the API
export function ApolloWrapper({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();

  // Get tokens from Redux store
  const session = useSelector(getSession);

  // Function to refresh the access token using the refresh token
  const refreshAccessTokenIfNeeded = async () => {
    try {
      const response = await fetch("/api/token", { method: "POST" });
      const data = await response.json();

      // Update the Redux store with the new access token
      dispatch(
        currentUserActions.refreshAccessToken({
          token: data.jwt,
          expiresAt: data.expiresAt,
        }),
      ); // Update Redux store with the new access token
      return data.jwt;
    } catch (error) {
      console.error("Error refreshing access token:", error);
    }
  };

  // Check if the access token is about to expire
  const isTokenExpiringSoon = () => {
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    return session.expiresAt - currentTime < 300; // Refresh if less than 5 minutes left
  };

  // Dynamically update the Authorization header on every request
  const authLink = setContext(async (_, { headers }) => {
    // Get the latest token from Redux
    // Or fetch a new token and use that
    let latestToken = session.jwt;
    // Refresh the token if it's about to expire
    if (isTokenExpiringSoon()) {
      latestToken = await refreshAccessTokenIfNeeded();
    }

    return {
      headers: {
        ...headers,
        Authorization: latestToken ? `Bearer ${latestToken}` : "",
      },
    };
  });

  // Create the Apollo Client only once
  const client = useMemo(() => {
    const httpLink = new HttpLink({
      uri: import.meta.env.VITE_GRAPHQL_URL,
    });

    const links = [authLink, httpLink];
    if (import.meta.env.VITE_ENV === "development") {
      links.unshift(errorLink);
    }
    return new ApolloClient({
      link: ApolloLink.from(links),
      connectToDevTools: true,
      cache,
    });
  }, [session?.jwt, session?.expiresAt]); // Apollo Client is only created once

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}

export {
  useFragment,
  useMutation,
  useQuery,
  useApolloClient,
  useSuspenseQuery,
  useLazyQuery,
  ApolloClient,
};
